// src/pages/personal/analytics/FeedbackSection/components/CommentsAnalysis/CommentsAnalysis.tsx
import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TEXT_STYLES } from '@/theme';
import { Flex } from '@/components/ui/Layout';
import { WordCloud } from './WordCloud';
import { CommentsTable } from './CommentsTable';
import { useResponsive } from '@/hooks/useResponsive';
import { formatSmartDate } from '@/utils/dateUtils';
import styles from '../../FeedbackSection.module.css';

interface WordData {
  word: string;
  count: number;
}

interface CommentData {
  id: string;
  text: string;
  rating: number;
  date: string;
  platform: string;
}

interface CommentsData {
  total: number;
  percentage_with_comments: number;
  top_words: WordData[];
  comments: CommentData[];
}

interface CommentsAnalysisProps {
  data: CommentsData | null;
  isLoading?: boolean;
  period?: string; // Добавляем параметр периода
  businessTimezone?: string; // Добавляем параметр часового пояса
}

// Тип для режимов отображения
type ViewMode = 'cloud' | 'table';

const CommentsAnalysis: React.FC<CommentsAnalysisProps> = ({ 
  data, 
  isLoading = false,
  period = 'month',
  businessTimezone = 'UTC'
}) => {
  const { t, i18n } = useTranslation('analytics');
  const { isMobile } = useResponsive();
  
  // Состояние для отслеживания режима отображения
  const [viewMode, setViewMode] = useState<ViewMode>('cloud');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  
  // ВАЖНО: Переместили useMemo до любых условных возвратов
  const processedComments = useMemo(() => {
    if (!data || !data.comments) return [];
    
    return data.comments.map(comment => {
      // Преобразуем дату комментария для отображения с учетом часового пояса бизнеса
      const formattedDate = formatSmartDate(comment.date, true, i18n.language);
      
      // Можно добавить логику группировки комментариев по периодам
      // В зависимости от значения параметра period
      
      return {
        ...comment,
        formattedDate
      };
    });
  }, [data, businessTimezone, i18n.language, period]); // добавил period в зависимости
  
  // Подготовка данных для облака слов
  const wordCloudData = useMemo(() => {
    return data?.top_words?.map(item => ({
      text: item.word,
      value: item.count
    })) || [];
  }, [data]);
  
  // Если нет данных, показываем пустой компонент
  if (!data && !isLoading) {
    return (
      <div>
        <div className="p-4 border-b bg-gray-50">
          <h3 className={TEXT_STYLES.h3}>{t('feedback.comments.title')}</h3>
        </div>
        <div className="p-4 h-[300px] flex items-center justify-center">
          <p className="text-gray-500">{t('no_data')}</p>
        </div>
      </div>
    );
  }
  
  // Структура для селектора режимов отображения
  const viewModes = [
    { value: 'cloud', label: t('feedback.comments.view_cloud') },
    { value: 'table', label: t('feedback.comments.view_table') }
  ];
  
  // Текущий выбранный режим
  const currentViewMode = viewModes.find(m => m.value === viewMode) || viewModes[0];
  
  // Обработчик изменения режима отображения
  const handleViewModeChange = (newMode: ViewMode) => {
    setViewMode(newMode);
    setIsDropdownOpen(false);
  };
  
  // Обработчик переключения выпадающего списка
  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };
  
  // Компонент для отображения состояния загрузки
  const renderLoading = () => (
    <div className="h-[300px] flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#245D33] mx-auto"></div>
        <p className="mt-2 text-gray-600">{t('loading')}</p>
      </div>
    </div>
  );
  
  return (
    <div>
      <div className="p-4 border-b bg-gray-50">
        <Flex 
          justify="between" 
          align="center"
          direction={isMobile ? "column" : "row"}
          gap={isMobile ? "sm" : "none"}
        >
          <h3 className={TEXT_STYLES.h3}>{t('feedback.comments.title')}</h3>
          
          {/* Селектор режимов отображения */}
          <div className={styles.viewModeSelector}>
            <button
              className={styles.viewModeButton}
              onClick={toggleDropdown}
            >
              <span>{currentViewMode.label}</span>
              <svg
                className="ml-2 h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
            
            {isDropdownOpen && (
              <div className={styles.viewModeDropdown}>
                <ul>
                  {viewModes.map((mode) => (
                    <li key={mode.value}>
                      <button
                        className={`${styles.viewModeItem} ${
                          mode.value === viewMode ? styles.viewModeItemActive : ''
                        }`}
                        onClick={() => handleViewModeChange(mode.value as ViewMode)}
                      >
                        {mode.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </Flex>
      </div>
      
      <div className="p-4">
        {isLoading ? (
          renderLoading()
        ) : data && data.total > 0 ? (
          <>
            {viewMode === 'cloud' ? (
              // Отображение облака слов
              <Flex 
                direction={isMobile ? "column" : "row"} 
                className={styles.commentsContainer}
                gap="md"
              >
                <div className="w-full sm:w-1/3 mb-4 sm:mb-0">
                  <div className={styles.metricSummary}>
                    <h4 className="font-medium mb-4">{t('feedback.comments.summary')}</h4>
                    <div className="space-y-3">
                      <div>
                        <div className={styles.metricLabel}>{t('feedback.comments.total')}</div>
                        <div className={styles.metricValue}>{data.total}</div>
                      </div>
                      <div>
                        <div className={styles.metricLabel}>{t('feedback.comments.percentage')}</div>
                        <div className={styles.metricValue}>{data.percentage_with_comments.toFixed(1)}%</div>
                      </div>
                      <div>
                        <div className={styles.metricLabel}>{t('feedback.comments.unique_words')}</div>
                        <div className={styles.metricValue}>{data.top_words.length}</div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Используем унифицированный класс chartContainer */}
                <div className="w-full sm:w-2/3">
                  <div className={styles.chartContainer}>
                    {wordCloudData.length > 0 ? (
                      <WordCloud data={wordCloudData} />
                    ) : (
                      <div className="h-full flex items-center justify-center">
                        <p className="text-gray-500">{t('feedback.comments.no_words')}</p>
                      </div>
                    )}
                  </div>
                </div>
              </Flex>
            ) : (
              // Отображение таблицы комментариев
              <div className="mt-2">
                <CommentsTable 
                  comments={processedComments} 
                  wordCloudData={wordCloudData}
                  period={period}
                  businessTimezone={businessTimezone}
                />
              </div>
            )}
          </>
        ) : (
          <div className="h-[300px] flex items-center justify-center">
            <p className="text-gray-500">{t('no_data')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommentsAnalysis;